import { Api } from "../Api";
import { DataFrame, Method, Status } from "../DataFrame";
import { Guid } from "../Guid";
import { Helpers } from "../Helpers";
import { IDIService } from "../IDIService";
import { ConnectedAppsService } from "./Controller/ConnectedAppsService";
import { ConnectedApp, UpsertConnectedAppResult } from "./Controller/IConnectedAppsService";
import { ExalusConnectionService } from "./ExalusConnectionService";
import { BrokerEnvironment, IExalusConnectionService } from "./IExalusConnectionService";
import { ILoggerService } from "./Logging/ILoggerService";
import { LoggerService } from "./Logging/LoggerService";

interface IAndroidAutoCarPlayService extends IDIService {
    SetInstallation(installation: InstallationInfo): Promise<void>;
    ClearInstallation(): Promise<void>;
}

export class InstallationInfo {
    public InstallationId?: string;
    public InstallationType?: InstallationType
    public BrokerUrl?: string;
    public ControllerSerial?: string;
    public ControllerPin?: string;
    public AuthToken?: string;

    constructor()
    constructor(installationId?: string, installationType?: InstallationType, brokerUrl?: string) {
        this.InstallationId = installationId;
        this.InstallationType = installationType;
        this.BrokerUrl = brokerUrl;
    }
}

export enum InstallationType {
    Lavva,
    Wisniowski,
    ExalusHome
}

export class AndroidAutoCarPlayService implements IAndroidAutoCarPlayService {
    public static readonly ServiceName: string = "AndroidAutoCarPlayService";

    GetServiceName(): string {
        return AndroidAutoCarPlayService.ServiceName;
    }

    async SetInstallation(installation: InstallationInfo): Promise<void> {
        if (installation.InstallationId === undefined || installation.InstallationType === undefined)
            return Promise.reject(new Error("Invalid installation info, InstallationId and InstallationType are required"));

        if (installation.InstallationType != InstallationType.ExalusHome && installation.BrokerUrl === undefined)
            return Promise.reject(new Error("Broker url is required for non-ExalusHome installations"));

        Api.Get<ILoggerService>(LoggerService.ServiceName).Info(AndroidAutoCarPlayService.ServiceName, `Setting Android Auto/CarPlay installation: ${installation.InstallationType} (${installation.InstallationId}) broker: ${installation.BrokerUrl}`);

        if (!this.IsAndroidAutoAvailable() && !(Helpers.IsIosNative() && (window as any).webkit.messageHandlers.carPlay != undefined)) {
            Api.Get<ILoggerService>(LoggerService.ServiceName).Warning(AndroidAutoCarPlayService.ServiceName, "Android Auto/CarPlay is not available on this platform!");
            return Promise.resolve();
        }

        if (installation.InstallationType === InstallationType.ExalusHome) {
            let ex = Api.Get<ExalusConnectionService>(ExalusConnectionService.ServiceName);
            installation.ControllerSerial = ex.GetControllerSerialNumber();
            installation.ControllerPin = ex.GetControllerPin();
            let serv = await ex.GetConnectedBrokerInfoAsync(5_000);


            switch (serv.Env) {
                case BrokerEnvironment.Development:
                    installation.BrokerUrl = "dev-broker.tr7.pl:4431";
                    break;
                default:
                    installation.BrokerUrl = "services-broker.tr7.pl";
                break;
            }
 
            try {
                let token = await this.GetTokenFromController();

                if (token)
                    installation.AuthToken = token;
                else
                    Api.Get<ILoggerService>(LoggerService.ServiceName).Warning(AndroidAutoCarPlayService.ServiceName, "Failed to get auth token from controller, Android Auto/CarPlay integration might not work properly!");
            }
            catch (err) {
                Api.Get<ILoggerService>(LoggerService.ServiceName).Error(AndroidAutoCarPlayService.ServiceName, "Error while getting auth token from controller, Android Auto/CarPlay integration might not work properly! \n" + err);
            }
        }

        if (this.IsAndroidAutoAvailable())
            (navigator as any).AndroidAuto.SetInstallation(installation);

        else if (Helpers.IsIosNative() && (window as any).webkit.messageHandlers.carPlay != undefined)
            (window as any).webkit.messageHandlers.carPlay.postMessage(installation);

        else
            return Promise.reject(new Error("Android Auto/CarPlay is not available"));

        return Promise.resolve();
    }

    async GetTokenFromController(): Promise<string | null> {
        if (this.IsAndroidAutoAvailable() || (Helpers.IsIosNative() && (window as any).webkit.messageHandlers.carPlay != undefined)) {
            let response = await Api.Get<IExalusConnectionService>(ExalusConnectionService.ServiceName).SendAndWaitForResponseAsync<TokenResponse>(new GetTokenRequest(), 12000, false);

            switch (response?.Status) {
                case Status.OK:
                    return Promise.resolve(response?.Data?.Token || null);
            }
        }

        return Promise.resolve(null);
    }

    IsAndroidAutoAvailable(): boolean {
        return (navigator as any).AndroidAuto !== undefined;
    }

    ClearInstallation(): Promise<void> {
        if (this.IsAndroidAutoAvailable())
            (navigator as any).AndroidAuto.SetInstallation(null);

        if (Helpers.IsIosNative() && (window as any).webkit.messageHandlers.carPlay != undefined)
            (window as any).webkit.messageHandlers.carPlay.postMessage(null);
        else
            return Promise.reject(new Error("Android Auto/CarPlay is not available"));

        return Promise.resolve();
    }
}

class GetTokenRequest extends DataFrame<any> {
    constructor() {
        super();
        this.Resource = "/webapibridge/get-token";
        this.Method = Method.Get;
    }
}

class TokenResponse {
    public Token?: string;
}
